import { GoogleMap, Marker, InfoWindow, Polyline, useLoadScript } from '@react-google-maps/api';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './App.css';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

const PUTANJA_OPCIJE = {
  strokeColor: 'red',
  strokeOpacity: 1,
  strokeWeight: 3.0,
};

const SARAJEVO = { lat: 43.8563, lng: 18.4131 };

const jeBroj = (v) => v !== null && v !== '' && !Number.isNaN(parseFloat(v));

// Ikonica markera na mapi: /assets/icons/navbar/icon_{tip}_foreground.png
const getIconUrl = (tip) => {
  const normalized = (tip || '').toString().trim().toLowerCase().replace(/\s+/g, '_');

  const iconMap = {
    ker_posavski_gonic: 'icon_ker_posavski_gonic_foreground.png',
    ker_srpski_trobojni_gonic: 'icon_ker_srpski_trobojni_gonic_foreground.png',
    ker_istarski_kratkodlaki_gonic: 'icon_ker_istarski_kratkodlaki_gonic_foreground.png',
    ker_istarski_ostrodlaki_gonic: 'icon_ker_istarski_ostrodlaki_gonic_foreground.png',
    ker_balkanski_gonic: 'icon_ker_balkanski_gonic_foreground.png',
    ker_bosanski_ostrodlaki_gonic: 'icon_ker_bosanski_ostrodlaki_gonic_foreground.png',
    ker_crnogorski_planinski_gonic: 'icon_ker_crnogorski_planinski_gonic_foreground.png',
    ker_brak_jazavcar: 'icon_ker_brak_jazavcar_foreground.png',
    ker_slovacki_kopov: 'icon_ker_slovacki_kopov_foreground.png',
    ker_plot: 'icon_ker_plot_foreground.png',
    ker_bigl: 'icon_ker_bigl_foreground.png',
    ker_nlt: 'icon_ker_nlt_foreground.png',
    krava: 'icon_krava_foreground.png',
    bik: 'icon_bik_foreground.png',
    ovca: 'icon_ovca_foreground.png',
    koza: 'icon_koza_foreground.png',
    konj: 'icon_konj_foreground.png',
    auto: 'icon_auto_foreground.png',
    motocikl: 'icon_motocikl_foreground.png',
    bicikl: 'icon_bicikl_foreground.png',
    smartphone: 'icon_smartphone_foreground.png',
  };

  const filename = iconMap[normalized] || 'icon_ker_balkanski_gonic_foreground.png';
  return `/assets/icons/navbar/${filename}`;
};

// ----------------------------------------------------------------------
// Gradijent putanje: prva boja = najstarija tačka, druga boja = najnovija tačka.
// Nazivi su isti kao vrijednosti kolone bojaputanje u bazi.

const BOJE = {
  plava: '#1E88E5',
  crvena: '#E53935',
  zelena: '#43A047',
  zuta: '#FDD835',
  cian: '#00BCD4',
  narandzasta: '#FB8C00',
  ljubicasta: '#8E24AA',
  roza: '#EC407A',
  tirkizna: '#1DE9B6',
};

const GRADIJENTI = {
  'Plavo-Crvena': [BOJE.plava, BOJE.crvena],
  'Zeleno-Zuta': [BOJE.zelena, BOJE.zuta],
  'Zuto-Crvena': [BOJE.zuta, BOJE.crvena],
  'Cian-Zelena': [BOJE.cian, BOJE.zelena],
  'Plavo-Cian': [BOJE.plava, BOJE.cian],
  'Narandzasto-Plava': [BOJE.narandzasta, BOJE.plava],
  'Zuto-Ljubicasta': [BOJE.zuta, BOJE.ljubicasta],
  'Ljubicasto-Zuta': [BOJE.ljubicasta, BOJE.zuta],
  'Roza-Zelena': [BOJE.roza, BOJE.zelena],
  'Tirkizno-Crvena': [BOJE.tirkizna, BOJE.crvena],
};

const DEFAULT_GRADIJENT = GRADIJENTI['Plavo-Crvena'];

// Najviše ovoliko obojenih dijelova po putanji (više = glađi prelaz, sporija mapa)
const BROJ_DIJELOVA = 40;

// Interpolacija između dvije hex boje, t od 0 do 1
function interpoliraj(od, doBoje, t) {
  const a = parseInt(od.slice(1), 16);
  const b = parseInt(doBoje.slice(1), 16);
  const kanal = (shift) => {
    const x = (a >> shift) & 255;
    const y = (b >> shift) & 255;
    return Math.round(x + (y - x) * t);
  };
  const boja = (kanal(16) << 16) | (kanal(8) << 8) | kanal(0);
  return `#${boja.toString(16).padStart(6, '0')}`;
}

// Putanja podijeljena na dijelove, svaki sa svojom bojom.
// Susjedni dijelovi dijele krajnju tačku, pa linija nema prekida.
function GradijentPutanja({ path, bojaputanje }) {
  const dijelovi = useMemo(() => {
    if (!path || path.length < 2) return [];
    const [pocetna, krajnja] = GRADIJENTI[String(bojaputanje || '').trim()] || DEFAULT_GRADIJENT;

    const brojDijelova = Math.min(BROJ_DIJELOVA, path.length - 1);
    const korak = (path.length - 1) / brojDijelova;

    const rezultat = [];
    for (let i = 0; i < brojDijelova; i += 1) {
      const od = Math.round(i * korak);
      const doIndeksa = Math.round((i + 1) * korak);
      rezultat.push({
        tacke: path.slice(od, doIndeksa + 1),
        boja: interpoliraj(pocetna, krajnja, brojDijelova === 1 ? 1 : i / (brojDijelova - 1)),
      });
    }
    return rezultat;
  }, [path, bojaputanje]);

  return dijelovi.map((d, i) => (
    <Polyline
      key={i}
      path={d.tacke}
      options={{ ...PUTANJA_OPCIJE, strokeColor: d.boja }}
    />
  ));
}

// ----------------------------------------------------------------------
// Mapa za jedan uređaj: IMEI dolazi iz localStorage (postavlja ga dugme "Mapa" u tabelama)

function Mapa() {
  const [device, setDevice] = useState(null);
  const [path, setPath] = useState([]);
  const [marker, setMarker] = useState(null);
  const [center, setCenter] = useState(SARAJEVO);
  const [infoOpen, setInfoOpen] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ok | nema-uredjaja | nema-lokacija | greska

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  // Učitaj uređaj i njegovu putanju jednom, pri otvaranju stranice
  useEffect(() => {
    const IMEI = localStorage.getItem('IMEI');

    const ucitaj = async () => {
      if (!IMEI) {
        setStatus('nema-uredjaja');
        return;
      }

      try {
        const devRes = await axios.get(`${SERVER_URL}/devices?IMEI=${IMEI}`);
        const lista = Array.isArray(devRes.data) ? devRes.data : [];
        const d = lista.find((x) => String(x.IMEI) === String(IMEI)) || lista[0];

        if (!d) {
          setStatus('nema-uredjaja');
          return;
        }
        setDevice(d);

        const gpsRes = await axios.get(`${SERVER_URL}/gpsrecords?IMEI=${d.IMEI}`);
        const zapisi = (Array.isArray(gpsRes.data) ? gpsRes.data : [])
          .filter((z) => jeBroj(z.lat) && jeBroj(z.lng))
          .map((z) => ({ ...z, lat: parseFloat(z.lat), lng: parseFloat(z.lng) }))
          .sort((a, b) => String(a.datumvrijemelokacije).localeCompare(String(b.datumvrijemelokacije)))
          // ista koordinata ponovo poslana = stara lokacija bez fixa; zadrži samo prvu (kao Android)
          .filter(function bezPonavljanja(z) {
            const k = `${z.lat},${z.lng}`;
            if (this.has(k)) return false;
            this.add(k);
            return true;
          }, new Set());

        if (!zapisi.length) {
          setStatus('nema-lokacija');
          return;
        }

        const zadnji = zapisi[zapisi.length - 1];

        setPath(zapisi.map(({ lat, lng }) => ({ lat, lng })));
        setMarker({
          position: { lat: zadnji.lat, lng: zadnji.lng },
          vrijeme: zadnji.datumvrijemelokacije || zadnji.vrijemelokacije,
        });
        setCenter({ lat: zadnji.lat, lng: zadnji.lng });
        setStatus('ok');
      } catch (err) {
        console.log('Greška pri učitavanju mape', err);
        setStatus('greska');
      }
    };

    ucitaj();
  }, []);

  const handleOnLoad = (map) => {
    map.setMapTypeId('satellite');
  };

  const vlasnik = device?.subscriber;

  return (
    <>
      <div style={{ padding: '10px', fontFamily: 'sans-serif' }}>
        {status === 'loading' && <p>Učitavanje uređaja...</p>}
        {status === 'nema-uredjaja' && <p>Nije izabran uređaj. Otvorite mapu preko dugmeta "Mapa" u tabeli uređaja.</p>}
        {status === 'nema-lokacija' && <p>Uređaj {device?.ime} nema zabilježenih lokacija.</p>}
        {status === 'greska' && <p>Greška pri učitavanju podataka. Pokušajte ponovo.</p>}
        {status === 'ok' && device && (
          <p>
            <strong>{device.ime}</strong> ({device.tiptrackera}), vlasnik {vlasnik?.ime} {vlasnik?.prezime}. Zadnja
            lokacija: {marker?.vrijeme}. Tačaka na putanji: {path.length}.
          </p>
        )}
      </div>

      {isLoaded && (
        <GoogleMap
          mapContainerClassName="map-container"
          center={center}
          zoom={status === 'ok' ? 17 : 7}
          onLoad={handleOnLoad}
          onClick={() => setInfoOpen(false)}
        >
          {path.length > 1 && <GradijentPutanja path={path} bojaputanje={device?.bojaputanje} />}

          {marker && device && (
            <Marker
              position={marker.position}
              onClick={() => setInfoOpen(true)}
              icon={{
                url: getIconUrl(device.tip),
                scaledSize: new window.google.maps.Size(50, 50),
              }}
            >
              {infoOpen && (
                <InfoWindow position={marker.position} onCloseClick={() => setInfoOpen(false)}>
                  <div id="iw-container">
                    <div className="iw-title">{device.ime}</div>
                    <div className="iw-content">
                      <p>Model: {device.tiptrackera}</p>
                      <p>IMEI: {device.IMEI}</p>
                      <p>
                        Vlasnik: {vlasnik?.ime} {vlasnik?.prezime}
                      </p>
                      <p>Vrijeme: {marker.vrijeme}</p>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          )}
        </GoogleMap>
      )}
    </>
  );
}

export default Mapa;